import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import {
  activityMatchesQuery,
  buildActivityGroupSections,
  filterActivityGroupSections,
  GROUP_HEADER_CLASS,
  hasRealActivityGroups,
} from "./projectActivityGroupUtils";
import { useProjectActivityPickerData } from "./useProjectActivityPickerData";

function activityLabel(row) {
  return row?.display_title || row?.displayTitle || row?.title || `#${row?.id}`;
}

/**
 * Inline grouped multi-select for project activities (edit event, plan, etc.).
 * Groups are fetched separately so headers show even before every row has group_id.
 */
export function GroupedActivityChecklist({
  project,
  selectedIds = [],
  onToggle,
  disabled = false,
  excludeIds = [],
  emptyLabel,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const projectId = project?.id;
  const { activities, groups, loading, error } = useProjectActivityPickerData(projectId, {
    enabled: !!projectId && !disabled,
  });

  const excluded = useMemo(
    () => new Set((excludeIds || []).map((id) => String(id))),
    [excludeIds]
  );
  const selected = useMemo(
    () => new Set((selectedIds || []).map((id) => String(id))),
    [selectedIds]
  );

  const visible = useMemo(
    () => (activities || []).filter((row) => !excluded.has(String(row.id))),
    [activities, excluded]
  );

  const showHeaders = hasRealActivityGroups(visible, groups);
  const q = query.trim();
  const sections = useMemo(() => {
    const built = buildActivityGroupSections(visible, groups, {
      ungroupedLabel: t("project_activity.ungrouped"),
      hideEmptyGroups: Boolean(q),
    });
    if (!q) return built;
    return filterActivityGroupSections(
      built,
      (row) =>
        activityMatchesQuery(row, q) ||
        (row?.group?.title || "").toLowerCase().includes(q.toLowerCase())
    );
  }, [visible, groups, q, t]);

  if (!projectId) {
    return (
      <p className="text-sm text-slate-500">{t("project_activity.select_project_first")}</p>
    );
  }

  if (loading) {
    return <p className="text-sm text-slate-500">{t("Loading")}…</p>;
  }

  if (error) {
    return <p className="text-sm text-red-600">{t("project_activity.load_error")}</p>;
  }

  const count = sections.reduce((n, s) => n + s.activities.length, 0);
  if (count === 0 && !showHeaders) {
    return (
      <p className="text-sm text-slate-500">
        {emptyLabel || t("project_activity.empty")}
      </p>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="relative border-b border-slate-200 p-2">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("project_activity.search_placeholder")}
          className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:outline-none"
        />
      </div>
      <div className="max-h-72 overflow-y-auto">
        {count === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-slate-500">
            {t("project_activity.empty")}
          </p>
        ) : (
          sections.map((section) => (
            <div key={section.key}>
              {showHeaders ? (
                <div className={GROUP_HEADER_CLASS}>
                  <span className="truncate">
                    {section.title || t("project_activity.ungrouped")}
                  </span>
                  <span className="tabular-nums text-[11px] font-bold text-[#1463C7]">
                    {section.activities.length}
                  </span>
                </div>
              ) : null}
              {section.activities.map((row) => {
                const checked = selected.has(String(row.id));
                return (
                  <label
                    key={row.id}
                    className={`flex cursor-pointer items-center gap-3 border-b border-slate-100 px-4 py-2.5 text-sm hover:bg-slate-50 ${
                      showHeaders ? "pl-7" : ""
                    } ${checked ? "bg-blue-50" : ""}`}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-blue-600"
                      checked={checked}
                      onChange={() => onToggle?.(row, !checked)}
                    />
                    <span className="min-w-0 font-medium text-slate-900 truncate">
                      {activityLabel(row)}
                    </span>
                  </label>
                );
              })}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function activityChipLabel(row, ungroupedLabel = "") {
  const title = activityLabel(row);
  const group = row?.group?.title;
  if (group) return `${group} · ${title}`;
  if (ungroupedLabel && row?.group_id == null) return title;
  return title;
}
